import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import Paginator, {
  PaginatorForRDT,
} from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { isLevel2 } from "../../hoc/isLevel2";
import { useDebounce, useToggle } from "../../hooks/common";
import {
  UserDetailsUpdated,
  usePendingLevel2UserVerificationCount,
  usePendingLevel2UserVerifications,
} from "../../hooks/level-2-user-verification.hooks";
import style from "../superAdmin/registrationFields/registrationFields.module.css";
import { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";

const getRegistrationStatusColor = (status: number) => {
  switch (status) {
    case 0:
      return "#000";
    case 1:
      return "#000";
    case 2:
      return "#000";
    case 3:
      return "#000";
    case 4:
      return "#000000";
    case 5:
      return "#000";
    default:
      return "#000";
  }
};

const getRegistrationStatus = (status: number) => {
  switch (status) {
    case 0:
      return "Pending Level 2 Review";
    case 1:
      return "Approved";
    case 2:
      return "Pending Level 3 Review";
    case 3:
      return "Pending Resubmission";
    case 4:
      return "on Hold";
    case 5:
      return "Rejected";
    default:
      return "Pending Level 2 Review";
  }
};
function LevelTwoUserVerificationsList() {
  const navigate = useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"table" | "card">("table");
  const [page, setPage] = useState(1);
  const search = useDebounce(searchQuery);
  const [filterState, setFilterState] = useState<Array<Number>>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "name",
    sortOrder: 1 as number,
  });

  const toggleFilter = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setFilterOpen(currentState => {
      return !currentState;
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    };

    if (filterOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [filterOpen]);

  const { isLoading: isLoading1, data: counts } =
    usePendingLevel2UserVerificationCount({
      search,
      filters: filterState,
    });
  const { isLoading: isLoading2, data } = usePendingLevel2UserVerifications({
    page,
    search,
    limit: 10,
    sort: sortKey,
    sortVal: sortOrder,
    filters: filterState,
  });

  const setSelectedFilters = (val: number) => {
    if (filterState.some((e) => e === val)) {
      setFilterState([...filterState.filter((e) => e !== val)]);
    } else {
      setFilterState([...filterState, val]);
    }
  };

  const dropdownContent = (
    <div
      className={style.dropdownBox}
      style={{ right: "358px", marginTop: "-14px" }}
    >
      <ul>
        <Dropdown.Menu show className={style.DropDropMenu}>
          <div className={style.dropContent}>
            <Dropdown.Header className={style.DropTile}>
              User Registration Filter
            </Dropdown.Header>
            <ul>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    className={style.checkBoxDrop}
                    style={{ marginTop: "21px" }}
                    checked={filterState.includes(0)}
                    onChange={() => setSelectedFilters(0)}
                  />{" "}
                  <label>Pending Level 2 Review</label>
                </div>
              </li>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    className={style.checkBoxDrop}
                    style={{ marginTop: "21px" }}
                    checked={filterState.includes(3)}
                    onChange={() => setSelectedFilters(3)}
                  />
                  Pending Resubmission
                </div>
              </li>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    className={style.checkBoxDrop}
                    style={{ marginTop: "21px" }}
                    checked={filterState.includes(2)}
                    onChange={() => setSelectedFilters(2)}
                  />
                  Pending Level 3 Review
                </div>
              </li>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    style={{ width: "13px" }}
                    className={style.checkBoxDrop}
                    checked={filterState.includes(1)}
                    onChange={() => setSelectedFilters(1)}
                  />
                  Approved
                </div>
              </li>
            </ul>
          </div>
        </Dropdown.Menu>
      </ul>
    </div>
  );

  const columns = useMemo(
    (): TableColumn<UserDetailsUpdated>[] => [
      {
        name: "Sr. No.",
        sortable: true,
        sortField: "index",
        // selector: (d, index) => (page - 1) * 10 + (index ?? 0) + 1,
        selector: (d, index) => {
          const serialNumber = (page - 1) * 10 + (index ?? 0) + 1;
          return serialNumber.toString().padStart(3, '0');
        },
        center: true,
        grow: 1,
        width: "120px",
      },
      {
        name: "Name",
        sortable: true,
        cell: (d) => (<span className="text-center">{d.name || "-"}</span>),
        center: true,
        sortField: "name",
        grow: 2,
      },
      {
        name: "User Type",
        sortable: true,
        sortField: "user_type",
        selector: (d) => d.user_type || "-",
        center: true,
        width: "195px",
      },
      {
        name: "Status",
        sortable: true,
        center: true,
        sortField: "registration_status",
        cell: (d) => (
          <div className="flex justify-center">
            <p
              className="text-center whitespace-nowrap overflow-hidden overflow-ellipsis"
              style={{ color: getRegistrationStatusColor(d.regestration_status) }}
            >
              <span className="text-center">{getRegistrationStatus(d.regestration_status)}</span>
            </p>
          </div>
        ),
        grow: 3,
        width: "220px",
      },
      {
        name: "Action",
        center: true,
        grow: 0.5,
        cell: (e) => (
          // <button
          //   onClick={() => {
          //     navigate(`/level-2-user-verification`, { state: e });
          //   }}
          //   className="icon-button"
          // >
          //   <i className="m-0 fa fa-eye"></i>
          // </button>
          <div className="flex space-x-2">
            <ViewButton
              onClick={() => {
                navigate(`/level-2-user-verification`, { state: e });
              }}
            />
          </div>
        ),
      },
    ],
    [page, navigate]
  );

  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between mb-4 gap-4 flex-wrap md:flex-nowrap items-center">
        <h4 className="text-2xl font-bold">All Requests</h4>
        <div className="flex space-x-4 grow md:grow-0">
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
          >
            {view === "table" ? (
              <i className="fa fa-th text-primary m-0" />
            ) : (
              <i className="fa fa-list-ul text-primary m-0" />
            )}
          </button>
          <>
            <button
              className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
              onMouseDown={toggleFilter}
            >
              <i className="fa fa-filter text-primary m-0" />
            </button>
          </>
          <div className="grow md:grow-0 flex rounded-xl border border-[#968f8f] bg-white items-stretch overflow-hidden">
            <input
              type="text"
              placeholder="Search..."
              className="bg-none px-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="p-3 shadow-md flex justify-center items-center">
              <i className="fa fa-search text-primary m-0" />
            </div>
          </div>
        </div>
      </div>
      {filterOpen && dropdownContent && (
        <div
          ref={dropdownRef}
          className={style.dropdown}
          onMouseDown={(e) => {
            e.stopPropagation();
            // console.log("Clicked inside dropdown");
          }}
        >
          {dropdownContent}
        </div>
      )}
      {view === "table" ? (
        <BoxComponent>
          <DataTable
            pagination
            sortServer
            paginationServer
            className="mt-4 rounded-0"
            columns={columns}
            keyField="userId"
            data={data?.data || []}
            sortIcon={<SortIcon />}
            customStyles={tableCustomStyles}
            onChangePage={(p) => setPage(p)}
            paginationTotalRows={counts?.data || 0}
            progressPending={isLoading1 || isLoading2}
            onSort={(col, dir) => {
              setSort({
                sortKey: col.sortField || "",
                sortOrder: dir === "asc" ? 1 : -1,
              });
            }}
            paginationComponent={PaginatorForRDT}
          />
        </BoxComponent>
      ) : (
        <>
          <BoxComponent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(data?.data || []).map((e, index) => (
                <div
                  className="p-4 rounded-2xl bg-white shadow-lg border border-gray-300"
                  key={e.userId}
                >
                  <div className="flex justify-between items-start space-x-4 mb-4">
                    <div
                      className={
                        "text-center bg-yellow-500 text-grey-600 inline-block py-1 px-4 rounded-full text-base text-[12px] font-semibold"
                      }
                      style={{
                        color: getRegistrationStatusColor(
                          e.regestration_status
                        ),
                      }}
                    >
                      {getRegistrationStatus(e.regestration_status)}
                    </div>
                    {/* <button
                      onClick={() => {
                        navigate(`/level-2-user-verification`, { state: e });
                      }}
                      className="icon-button"
                    >
                      <i className="m-0 fa fa-eye"></i>
                    </button> */}
                    <div className="flex space-x-2">
                      <ViewButton
                        onClick={() => {
                          navigate(`/level-2-user-verification`, { state: e });
                        }}
                      />
                    </div>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">Name</p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{e.name}</p>
                  </div>
                  <div className="mb-2 flex">
                    <p className="font-bold text-base w-1/2">User Type</p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{e.user_type || "-"}</p>
                  </div>
                </div>
              ))}
            </div>
            <Paginator
              limit={10}
              page={page}
              setPage={setPage}
              count={counts?.data || 0}
            />
          </BoxComponent>
        </>
      )}
    </div>
  );
}

export default isLevel2(LevelTwoUserVerificationsList);
